// src/components/ReportArea.tsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    CircularProgress,
    Paper,
    TextField,
    IconButton,
    Button,
    List,
    ListItemButton,
    ListItemText,
    Tabs,
    Tab,
    Typography,
    Rating,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css';
import api from '../services/api';

interface ReportData {
    id: string;
    title: string;
    content: string;
    raw_data: string;
    rating: number;
}

interface ReportAreaProps {
    selectedReport: ReportData | null;
    isFetchingReportContent: boolean;
    handleGenerateReport: (tickerId: string) => void;
    isGeneratingReport: boolean;
}

interface Ticker {
    id: string;
    ticker: string;
    company_name: string;
}

const ReportArea: React.FC<ReportAreaProps> = ({
                                                   selectedReport,
                                                   isFetchingReportContent,
                                                   handleGenerateReport,
                                                   isGeneratingReport,
                                               }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<Ticker[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedTickerId, setSelectedTickerId] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'report' | 'raw_data'>('report');

    // Loader messages
    const loadingMessages = [
        '🔍 Analyzing Market Trends...',
        '📰 Performing Sentiment Analysis...',
        '📈 Conducting Technical Analysis...',
        '💡 Compiling Insights...',
        '📝 Generating Report...',
    ];
    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (isGeneratingReport) {
            timer = setInterval(() => {
                setLoadingMessageIndex((prevIndex) => (prevIndex + 1) % loadingMessages.length);
            }, 3000); // Change message every 3 seconds
        } else {
            setLoadingMessageIndex(0); // Reset when not generating
        }
        return () => {
            clearInterval(timer);
        };
    }, [isGeneratingReport]);

    const handleSearch = async () => {
        if (!searchQuery.trim()) return;
        setIsSearching(true);
        try {
            const response = await api.get('/ticker/search', {
                params: { query: searchQuery, market: 'stocks' },
            });
            setSearchResults(response.data);
        } catch (error) {
            console.error('Error searching tickers:', error);
            // Handle error appropriately
        } finally {
            setIsSearching(false);
        }
    };

    const handleGenerateReportClick = () => {
        if (selectedTickerId) {
            handleGenerateReport(selectedTickerId);
            setSearchResults([]);
            setSearchQuery('');
            setSelectedTickerId(null);
        }
    };

    const handleChangeView = (event: React.SyntheticEvent, newValue: 'report' | 'raw_data') => {
        setViewMode(newValue);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Title and Rating */}
            {selectedReport && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6">{selectedReport.title}</Typography>
                    <Rating value={selectedReport.rating} readOnly />
                </Box>
            )}

            {/* Tabs for Report and Raw Data */}
            <Tabs value={viewMode} onChange={handleChangeView} sx={{ mb: 2 }}>
                <Tab label="Report" value="report" />
                <Tab label="Raw Data" value="raw_data" />
            </Tabs>

            {/* Main Report Content Area */}
            <Box
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    minHeight: 0,
                    p: 2,
                    backgroundColor: 'background.default',
                    borderRadius: 2,
                    boxShadow: 1,
                    position: 'relative',
                    mb: 2,
                }}
            >
                {/* Generating or Fetching Loader */}
                {isGeneratingReport ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            textAlign: 'center',
                        }}
                    >
                        <CircularProgress />
                        <Typography variant="h6" sx={{ mt: 2 }}>
                            {loadingMessages[loadingMessageIndex]}
                        </Typography>
                    </Box>
                ) : isFetchingReportContent ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : selectedReport ? (
                    <Paper
                        elevation={3}
                        sx={{
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            wordBreak: 'break-word',
                        }}
                    >
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeHighlight]}
                        >
                            {viewMode === 'report' ? selectedReport.content : selectedReport.raw_data}
                        </ReactMarkdown>
                    </Paper>
                ) : (
                    <Typography variant="body1">Select a report to view its content.</Typography>
                )}
            </Box>

            {/* Search Results ABOVE the pinned search bar */}
            {searchResults.length > 0 && (
                <Box
                    sx={{
                        mb: 2,
                        maxHeight: '30vh',
                        overflowY: 'auto',
                        boxSizing: 'border-box',
                        borderRadius: 2,
                        boxShadow: 1,
                    }}
                >
                    <List disablePadding>
                        {searchResults.map((ticker) => (
                            <ListItemButton
                                key={ticker.id}
                                selected={selectedTickerId === ticker.id}
                                onClick={() => setSelectedTickerId(ticker.id)}
                            >
                                <ListItemText primary={ticker.ticker} secondary={ticker.company_name} />
                            </ListItemButton>
                        ))}
                    </List>
                </Box>
            )}

            {/* Pinned Search Bar at the Bottom */}
            <Box
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: 'background.paper',
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                    p: 1,
                    boxShadow: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search for tickers..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                        InputProps={{
                            endAdornment: (
                                <IconButton onClick={handleSearch} disabled={isSearching || !searchQuery.trim()}>
                                    {isSearching ? <CircularProgress size={24} /> : <SearchIcon />}
                                </IconButton>
                            ),
                        }}
                        sx={{ mr: 1 }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleGenerateReportClick}
                        disabled={!selectedTickerId || isGeneratingReport}
                    >
                        {isGeneratingReport ? <CircularProgress size={24} /> : 'Research'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default ReportArea;
